import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Dropdown, Form, Input, InputNumber, Modal, Segmented, Skeleton } from 'antd';
import type { MenuProps } from 'antd';
import {
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShareAltOutlined,
  SwapOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  projectService,
  Project,
  ProjectTask,
  ProjectTaskInput,
  TaskColumns,
  TaskStatus,
  TASK_STATUSES,
} from '../../services/projectService';
import { EmptyState, Pill, Tone } from '../ui';
import './ProjectPlanDrawer.css';

const STATUS_TONE: Record<TaskStatus, Tone> = { ready: 'neutral', in_progress: 'primary', done: 'success' };

interface DropTarget {
  status: TaskStatus;
  /** Position among the column's cards, not counting the one being dragged */
  index: number;
}

const byPosition = (a: ProjectTask, b: ProjectTask) => a.position - b.position || a.id - b.id;
const sumOf = (tasks: ProjectTask[]) => tasks.reduce((sum, task) => sum + task.percentage, 0);

/** The 422 message from the API (e.g. "at most 100%"), when there is one. */
const apiError = (error: any): string | undefined => {
  const errors = error?.response?.data?.errors;
  const first = errors && (Object.values(errors)[0] as string[] | undefined);
  return first?.[0] ?? error?.response?.data?.message;
};

/**
 * Projects → Plan: the project split into tasks/phases (remove old laminate, tile the bathroom,
 * paint the walls…), each worth a share of the project, on a Ready / In progress / Done board.
 * Cards are dragged between and within columns; the "⋯" menu does the same without a mouse.
 */
const ProjectPlanDrawer: React.FC<{
  project: Project | null;
  onClose: () => void;
  onTasksChange: (projectId: number, tasks: ProjectTask[]) => void;
  onEditProject: (project: Project) => void;
  onShare: (project: Project) => void;
}> = ({ project, onClose, onTasksChange, onEditProject, onShare }) => {
  const { t } = useTranslation();
  const [modal, modalContextHolder] = Modal.useModal();
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const columnRefs = useRef<Partial<Record<TaskStatus, HTMLDivElement | null>>>({});

  // Task form
  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ProjectTask | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const projectId = project?.id;

  const apply = (next: ProjectTask[]) => {
    setTasks(next);
    if (projectId) onTasksChange(projectId, next);
  };

  const load = async (id: number) => {
    setLoading(true);
    try {
      apply(await projectService.getTasks(id));
    } catch {
      setTasks([]);
      toast.error(t('projects.plan.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    setTasks([]);
    load(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const columns = useMemo(() => {
    const grouped: Record<TaskStatus, ProjectTask[]> = { ready: [], in_progress: [], done: [] };
    tasks.forEach((task) => (grouped[task.status] ?? grouped.ready).push(task));
    TASK_STATUSES.forEach((status) => grouped[status].sort(byPosition));
    return grouped;
  }, [tasks]);

  const planned = sumOf(tasks);
  const remaining = Math.max(0, 100 - planned);
  const done = sumOf(columns.done);
  const inProgress = sumOf(columns.in_progress);

  // ---------- Moving ----------

  /** Puts a task at `index` of `status` and saves the whole board (optimistic). */
  const moveTask = async (taskId: number, status: TaskStatus, index: number) => {
    if (!projectId) return;
    const order: TaskColumns = { ready: [], in_progress: [], done: [] };
    TASK_STATUSES.forEach((key) => {
      order[key] = columns[key].map((task) => task.id).filter((id) => id !== taskId);
    });
    order[status].splice(Math.min(index, order[status].length), 0, taskId);

    const unchanged = TASK_STATUSES.every((key) =>
      columns[key].map((task) => task.id).join(',') === order[key].join(','),
    );
    if (unchanged) return;

    const previous = tasks;
    const next = tasks.map((task) => {
      const key = TASK_STATUSES.find((candidate) => order[candidate].includes(task.id)) ?? task.status;
      return { ...task, status: key, position: order[key].indexOf(task.id) };
    });
    apply(next);

    try {
      apply(await projectService.reorderTasks(projectId, order));
    } catch {
      apply(previous);
      toast.error(t('projects.plan.moveError'));
    }
  };

  const handleDragStart = (event: React.DragEvent, task: ProjectTask) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(task.id));
    // Let the browser take its drag image before the card fades
    window.setTimeout(() => setDraggingId(task.id), 0);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  const handleDragOver = (event: React.DragEvent, status: TaskStatus) => {
    if (draggingId === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const cards = Array.from(
      columnRefs.current[status]?.querySelectorAll<HTMLElement>('[data-task-id]') ?? [],
    ).filter((element) => Number(element.dataset.taskId) !== draggingId);
    let index = cards.findIndex((element) => {
      const rect = element.getBoundingClientRect();
      return event.clientY < rect.top + rect.height / 2;
    });
    if (index === -1) index = cards.length;
    if (dropTarget?.status !== status || dropTarget.index !== index) setDropTarget({ status, index });
  };

  const handleDragLeave = (event: React.DragEvent, status: TaskStatus) => {
    const next = event.relatedTarget as Node | null;
    if (next && columnRefs.current[status]?.contains(next)) return;
    setDropTarget((current) => (current?.status === status ? null : current));
  };

  const handleDrop = (event: React.DragEvent, status: TaskStatus) => {
    event.preventDefault();
    const taskId = draggingId ?? Number(event.dataTransfer.getData('text/plain'));
    const index = dropTarget?.status === status ? dropTarget.index : columns[status].length;
    handleDragEnd();
    if (taskId) moveTask(taskId, status, index);
  };

  // ---------- Create / edit / delete ----------

  /** What this task may be worth: everything not taken by the other tasks. */
  const availableFor = (task: ProjectTask | null) => 100 - planned + (task?.percentage ?? 0);

  const openForm = (task: ProjectTask | null, status: TaskStatus = 'ready') => {
    setEditingTask(task);
    form.resetFields();
    form.setFieldsValue(
      task
        ? { title: task.title, description: task.description ?? '', percentage: task.percentage, status: task.status }
        : { title: '', description: '', percentage: remaining > 0 ? undefined : 0, status },
    );
    setFormOpen(true);
  };

  const handleSubmit = async (values: ProjectTaskInput) => {
    if (!projectId) return;
    const payload: ProjectTaskInput = {
      title: values.title.trim(),
      description: values.description?.trim() || null,
      percentage: values.percentage ?? 0,
      status: values.status,
    };
    setSaving(true);
    try {
      if (editingTask) {
        const saved = await projectService.updateTask(editingTask.id, payload);
        apply(tasks.map((task) => (task.id === saved.id ? saved : task)));
        toast.success(t('projects.plan.updated'));
      } else {
        const saved = await projectService.createTask(projectId, payload);
        apply([...tasks, saved]);
        toast.success(t('projects.plan.created'));
      }
      setFormOpen(false);
    } catch (error) {
      toast.error(apiError(error) ?? t('projects.plan.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (task: ProjectTask) => {
    modal.confirm({
      title: t('projects.plan.deleteConfirm'),
      content: task.title,
      okText: t('common.delete'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await projectService.deleteTask(task.id);
          apply(tasks.filter((item) => item.id !== task.id));
          toast.success(t('projects.plan.deleted'));
        } catch {
          toast.error(t('projects.plan.deleteError'));
        }
      },
    });
  };

  const taskMenu = (task: ProjectTask): MenuProps['items'] => [
    { key: 'edit', icon: <EditOutlined />, label: t('projects.plan.editTask'), onClick: () => openForm(task) },
    {
      key: 'move',
      icon: <SwapOutlined />,
      label: t('projects.plan.moveTo'),
      children: TASK_STATUSES.filter((status) => status !== task.status).map((status) => ({
        key: `move-${status}`,
        label: t(`projects.plan.columns.${status}`),
        onClick: () => moveTask(task.id, status, columns[status].length),
      })),
    },
    { type: 'divider' },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      danger: true,
      label: t('common.delete'),
      onClick: () => confirmDelete(task),
    },
  ];

  // ---------- Render ----------

  const renderCard = (task: ProjectTask) => (
    <div
      key={task.id}
      data-task-id={task.id}
      className={`ppl-card ppl-card--${task.status} ${draggingId === task.id ? 'is-dragging' : ''}`}
      draggable
      onDragStart={(event) => handleDragStart(event, task)}
      onDragEnd={handleDragEnd}
      onClick={() => openForm(task)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target === event.currentTarget) openForm(task);
      }}
      role="button"
      tabIndex={0}
    >
      <div className="ppl-card-head">
        <HolderOutlined className="ppl-card-grip" aria-hidden />
        <span className="ppl-card-title">
          {task.status === 'done' && <CheckCircleFilled className="ppl-card-check" aria-hidden />}
          {task.title}
        </span>
        <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <Dropdown menu={{ items: taskMenu(task) }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label={t('projects.schedule.moreActions')} />
          </Dropdown>
        </span>
      </div>
      {task.description ? <p className="ppl-card-description">{task.description}</p> : null}
      <div className="ppl-card-foot">
        <span className="ppl-card-share">{t('projects.plan.ofProject', { percent: task.percentage })}</span>
        <div className="ppl-card-bar" aria-hidden>
          <div style={{ width: `${task.percentage}%` }} />
        </div>
      </div>
    </div>
  );

  const renderColumn = (status: TaskStatus) => {
    const list = columns[status];
    const target = dropTarget?.status === status ? dropTarget.index : null;
    const indicator = <div key="drop-indicator" className="ppl-drop-indicator" aria-hidden />;
    const children: React.ReactNode[] = [];
    let visibleIndex = 0;
    list.forEach((task) => {
      if (task.id !== draggingId) {
        if (target === visibleIndex) children.push(indicator);
        visibleIndex += 1;
      }
      children.push(renderCard(task));
    });
    if (target !== null && target >= visibleIndex) children.push(indicator);

    return (
      <section
        key={status}
        className={`ppl-column ppl-column--${status} ${target !== null ? 'is-over' : ''}`}
        onDragOver={(event) => handleDragOver(event, status)}
        onDragLeave={(event) => handleDragLeave(event, status)}
        onDrop={(event) => handleDrop(event, status)}
        aria-label={t(`projects.plan.columns.${status}`)}
      >
        <header className="ppl-column-head">
          <span className="ppl-column-dot" aria-hidden />
          <span className="ppl-column-name">{t(`projects.plan.columns.${status}`)}</span>
          <span className="ppl-column-count">{list.length}</span>
          <span className="ppl-column-sum">{sumOf(list)}%</span>
        </header>
        <div className="ppl-column-body" ref={(element) => {
          columnRefs.current[status] = element;
        }}>
          {children}
          {list.length === 0 && target === null && (
            <div className="ppl-column-empty">{t('projects.plan.dropHere')}</div>
          )}
        </div>
        <button type="button" className="ppl-column-add" onClick={() => openForm(null, status)}>
          <PlusOutlined aria-hidden /> {t('projects.plan.addTask')}
        </button>
      </section>
    );
  };

  const formStatus: TaskStatus = Form.useWatch('status', form) ?? 'ready';
  const available = availableFor(editingTask);

  return (
    <Drawer
      title={
        project ? (
          <div className="ppl-title">
            <span className="ppl-title-name">{project.name}</span>
            <span className="ppl-title-sub">{t('projects.plan.title')}</span>
          </div>
        ) : null
      }
      placement="right"
      onClose={onClose}
      open={!!project}
      width="min(1180px, 100vw)"
      className="ppl-drawer"
      extra={
        project ? (
          <div className="ppl-extra">
            <Button
              type="text"
              icon={<ReloadOutlined spin={loading} />}
              onClick={() => load(project.id)}
              disabled={loading}
              aria-label={t('dashboard.refresh')}
            />
            <Button icon={<ShareAltOutlined />} onClick={() => onShare(project)}>
              <span className="ppl-hide-narrow">{t('projects.share.menu')}</span>
            </Button>
            <Button icon={<EditOutlined />} onClick={() => onEditProject(project)}>
              <span className="ppl-hide-narrow">{t('projects.actions.editProject')}</span>
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
              {t('projects.plan.addTask')}
            </Button>
          </div>
        ) : null
      }
    >
      {modalContextHolder}
      {loading && tasks.length === 0 ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={<UnorderedListOutlined />}
          text={t('projects.plan.empty')}
          action={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
              {t('projects.plan.addFirstTask')}
            </Button>
          }
        />
      ) : (
        <>
          <div className="ppl-summary">
            <div className="ppl-summary-main">
              <span className="ppl-summary-value">{done}%</span>
              <span className="ppl-summary-label">{t('projects.plan.completed')}</span>
            </div>
            <div className="ppl-summary-bars">
              <div
                className="ppl-stack"
                role="progressbar"
                aria-valuenow={done}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('projects.plan.completed')}
              >
                <div className="ppl-stack-done" style={{ width: `${done}%` }} />
                <div className="ppl-stack-progress" style={{ width: `${inProgress}%` }} />
                <div className="ppl-stack-ready" style={{ width: `${sumOf(columns.ready)}%` }} />
              </div>
              <div className="ppl-legend">
                {TASK_STATUSES.map((status) => (
                  <span key={status} className={`ppl-legend-item ppl-legend-item--${status}`}>
                    <span className="ppl-legend-dot" aria-hidden />
                    {t(`projects.plan.columns.${status}`)} {sumOf(columns[status])}%
                  </span>
                ))}
                {remaining > 0 && (
                  <span className="ppl-legend-item ppl-legend-item--unplanned">
                    <span className="ppl-legend-dot" aria-hidden />
                    {t('projects.plan.unplanned')} {remaining}%
                  </span>
                )}
              </div>
            </div>
            <div className="ppl-summary-status">
              {remaining > 0 ? (
                <Pill tone="warning">{t('projects.plan.plannedOf', { planned })}</Pill>
              ) : (
                <Pill tone="success">{t('projects.plan.fullyPlanned')}</Pill>
              )}
            </div>
          </div>
          {remaining > 0 && (
            <div className="ppl-hint">
              <InfoCircleOutlined aria-hidden />
              <span>{t('projects.plan.remainingHint', { remaining })}</span>
            </div>
          )}
          <div className="ppl-board">{TASK_STATUSES.map(renderColumn)}</div>
        </>
      )}

      <Modal
        title={editingTask ? t('projects.plan.editTask') : t('projects.plan.newTask')}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={() => form.submit()}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
          <Form.Item
            name="title"
            label={t('projects.plan.taskTitle')}
            rules={[{ required: true, whitespace: true, message: t('projects.plan.titleRequired') }]}
          >
            <Input maxLength={255} placeholder={t('projects.plan.titlePlaceholder')} autoFocus />
          </Form.Item>
          <Form.Item name="description" label={t('projects.plan.description')}>
            <Input.TextArea rows={3} maxLength={5000} placeholder={t('projects.plan.descriptionPlaceholder')} />
          </Form.Item>
          <Form.Item
            name="percentage"
            label={t('projects.plan.percentage')}
            extra={
              <span className="ppl-available">
                {t('projects.plan.available', { available })}
                {available > 0 && (
                  <button
                    type="button"
                    className="e22-link-button"
                    onClick={() => form.setFieldsValue({ percentage: available })}
                  >
                    {t('projects.plan.useAvailable', { available })}
                  </button>
                )}
              </span>
            }
            rules={[
              { required: true, message: t('projects.plan.percentageRequired') },
              {
                validator: (_, value?: number | null) =>
                  value == null || value <= available
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('projects.plan.tooMuch', { available }))),
              },
            ]}
          >
            <InputNumber min={0} max={100} precision={0} addonAfter="%" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="status" label={t('projects.plan.status')}>
            <Segmented
              block
              options={TASK_STATUSES.map((status) => ({
                value: status,
                label: (
                  <span className={`ppl-status-option ppl-status-option--${status}`}>
                    {t(`projects.plan.columns.${status}`)}
                  </span>
                ),
              }))}
            />
          </Form.Item>
          {formStatus === 'done' && <Pill tone={STATUS_TONE.done}>{t('projects.plan.countsAsDone')}</Pill>}
        </Form>
      </Modal>
    </Drawer>
  );
};

export default ProjectPlanDrawer;
